import Vue from "vue";
import axios from "axios";
import { streamMessage } from "@/services/assistantStream";

let controller = null;
// After done the turn is over for the user, but the reader stays open for the tail (suggested replies). The next send,
// a new conversation or any reset aborts it.
let tailController = null;

function abortTail() {
	if (tailController) {
		tailController.abort();
		tailController = null;
	}
}
// Rewords a burst-limit error row once its wait is over; cancelled whenever the conversation or the store is reset.
let burstTimer = null;

function clearBurstTimer() {
	if (burstTimer) {
		clearTimeout(burstTimer);
		burstTimer = null;
	}
}

const REVOKED_TEXT = "The assistant has been turned off for your account.";
const AUTH_TEXT = "Your session has ended. Sign in again to use the assistant.";

// Smooth streaming. The model host (Azure Foundry buffers a whole completion behind its content filter) and the
// network both deliver text in bursts; an answer that lands in one frame reads as a blurt. Deltas are queued and
// revealed on animation frames at a pace that scales with the backlog: a few characters per frame when the stream
// is keeping up, and enough to drain a large burst in about CATCH_UP_MS, so the reveal never trails the real stream
// by more than about a second. Flushed at once on stop, on errors and when the tab is hidden (frames pause there).
const CATCH_UP_MS = 700;
const MIN_CHARS_PER_FRAME = 6;
let pendingText = "";
let revealFrame = null;
let lastRevealAt = 0;
let drainWaiters = [];

function settleDrainWaiters() {
	drainWaiters.splice(0).forEach((resolve) => resolve());
}

function cancelRevealFrame() {
	if (revealFrame !== null) {
		cancelAnimationFrame(revealFrame);
		revealFrame = null;
	}
}

function revealStep(commit, now) {
	const elapsed = Math.max(1, now - lastRevealAt);
	lastRevealAt = now;
	let take = Math.max(MIN_CHARS_PER_FRAME, Math.ceil((pendingText.length * elapsed) / CATCH_UP_MS));
	take = Math.min(take, pendingText.length);
	// Never split a surrogate pair.
	const code = pendingText.charCodeAt(take - 1);
	if (code >= 0xd800 && code <= 0xdbff && take < pendingText.length) take += 1;
	commit("append_text", pendingText.slice(0, take));
	pendingText = pendingText.slice(take);
	if (pendingText.length) revealFrame = requestAnimationFrame((next) => revealStep(commit, next));
	else {
		revealFrame = null;
		settleDrainWaiters();
	}
}

function enqueueText(commit, delta) {
	pendingText += delta;
	if (typeof document !== "undefined" && document.hidden) {
		flushText(commit);
		return;
	}
	if (revealFrame === null) {
		lastRevealAt = performance.now();
		revealFrame = requestAnimationFrame((now) => revealStep(commit, now));
	}
}

// Shows whatever is queued right away (stop, errors, hidden tab).
function flushText(commit) {
	cancelRevealFrame();
	if (pendingText) commit("append_text", pendingText);
	pendingText = "";
	settleDrainWaiters();
}

// Drops the queue without showing it (restart, new conversation, reset).
function discardText() {
	cancelRevealFrame();
	pendingText = "";
	settleDrainWaiters();
}

function textDrained() {
	if (!pendingText.length && revealFrame === null) return Promise.resolve();
	return new Promise((resolve) => drainWaiters.push(resolve));
}

const state = {
	open: false,
	conversationId: null,
	messages: [], // { id, role: "user"|"assistant", text, cards: [{ type: "search"|"attachment"|"faq", ... }], tools: [], truncated, suggestions: [] or null }
	streaming: false,
	progress: [],
	usage: null, // { used, limit, remaining, resetsOn, exempt }
	lastSearch: null, // { filters, totalCount, note, at }: Product Search applies the filters and runs its own search
	pendingSearch: null, // filters to apply when Product Search mounts
	formDirty: false,
	formFilters: null,
	openProduct: null, // { productId, name, vendor }: the product open in Product Search's detail view
	notice: null, // { kind: "quota"|"off"|"closed"|"auth", text }: replaces the composer
	error: null, // { text, retryable, lastText, turnKept }: a row under the transcript; the composer stays
	view: "chat", // "chat"|"list": the transcript or the Conversations panel
	conversations: [], // { conversationId, title, titleSetByUser, createdAt, lastActivityAt, isClosed, turnCount }
	conversationsTotal: 0,
	conversationsSearch: "",
	conversationsPage: 1,
	conversationsLoading: false,
	readOnly: false, // a closed conversation reopened from the list: the composer is replaced by a notice
	title: null, // the open conversation's title
	titleSetByUser: false, // whether the open conversation's title is one the user set
	listError: null, // a failed list action, shown in the Conversations panel only
	pendingTitle: null, // "Continue in a new conversation" carries the closed conversation's title to the next send
	pendingTitleSetByUser: false, // whether pendingTitle was set by the user (kept by the classifier)
	draftEmpty: true, // whether the composer is empty; suggested replies are never shown under a draft the user is typing
};

const getters = {
	isAvailable: (state, getters, rootState, rootGetters) =>
		!!rootGetters.userInfo?.assistantEnabled && !!rootState.system.assistantEnabled,
	currentAssistantMessage: (state) => {
		const last = state.messages[state.messages.length - 1];
		return last && last.role === "assistant" ? last : null;
	},
};

let nextId = 1;
// Only the latest list request may commit, so a slow page cannot overwrite a newer search.
let listRequest = 0;
// Only the latest open may replace the transcript.
let openRequest = 0;

// Every reset cancels opens and list loads still in flight, so a pending GET cannot land in the reset store.
function cancelPendingLoads(commit) {
	openRequest++;
	listRequest++;
	commit("set_conversations_loading", false);
}

// A 401 or 403 from a list action ends the assistant session the same way the chat path does. Returns true when handled.
function endSessionOnAccessError(commit, dispatch, status) {
	if (status !== 401 && status !== 403) return false;
	dispatch("stop");
	discardText();
	cancelPendingLoads(commit);
	commit("reset_conversation");
	commit("set_pending_title", null);
	commit("set_list_error", null);
	commit("set_notice", status === 403 ? { kind: "off", text: REVOKED_TEXT } : { kind: "auth", text: AUTH_TEXT });
	return true;
}

const mutations = {
	set_open(state, open) {
		state.open = open;
	},
	// Leaves pendingTitle alone: continueInNew sets it right after the reset.
	reset_conversation(state) {
		clearBurstTimer();
		discardText();
		state.conversationId = null;
		// Replacing the list drops every suggestion with it.
		state.messages = [];
		state.progress = [];
		state.lastSearch = null;
		state.notice = null;
		state.error = null;
		state.readOnly = false;
		state.title = null;
		state.titleSetByUser = false;
		state.view = "chat";
	},
	set_conversation(state, id) {
		state.conversationId = id;
	},
	push_user(state, text) {
		state.messages.push({ id: nextId++, role: "user", text, cards: [], tools: [], truncated: false });
	},
	push_assistant(state) {
		state.messages.push({
			id: nextId++,
			role: "assistant",
			text: "",
			cards: [],
			tools: [],
			truncated: false,
			suggestions: [],
		});
	},
	append_text(state, delta) {
		const last = state.messages[state.messages.length - 1];
		if (last && last.role === "assistant") last.text += delta;
	},
	push_card(state, card) {
		const last = state.messages[state.messages.length - 1];
		if (last && last.role === "assistant") last.cards.push(card);
	},
	push_tool(state, label) {
		const last = state.messages[state.messages.length - 1];
		if (last && last.role === "assistant" && !last.tools.includes(label)) last.tools.push(label);
	},
	set_truncated(state, options) {
		const last = state.messages[state.messages.length - 1];
		if (last && last.role === "assistant") {
			last.truncated = true;
			// Set when the user pressed Stop, so the bubble says so instead of "cut short".
			Vue.set(last, "stopped", !!(options && options.stopped));
		}
	},
	// Suggested replies land on the turn's own assistant message, and only while it is still the latest one.
	set_suggestions(state, { message, replies }) {
		// The user started typing their own next message before the suggestions arrived.
		if (!state.draftEmpty) return;
		const last = state.messages[state.messages.length - 1];
		if (last && last === message) Vue.set(last, "suggestions", replies);
	},
	set_draft_empty(state, empty) {
		state.draftEmpty = !!empty;
	},
	clear_suggestions(state) {
		state.messages.forEach((m) => {
			if (m.suggestions && m.suggestions.length) m.suggestions = [];
		});
	},
	clear_assistant_text(state) {
		const last = state.messages[state.messages.length - 1];
		if (last && last.role === "assistant") {
			last.text = "";
			last.cards = [];
		}
	},
	drop_last_turn(state) {
		// Remove the trailing optimistic assistant and user bubbles (a reset may already have emptied the list).
		const msgs = state.messages;
		if (msgs.length && msgs[msgs.length - 1].role === "assistant") msgs.pop();
		if (msgs.length && msgs[msgs.length - 1].role === "user") msgs.pop();
	},
	drop_last_assistant(state) {
		const msgs = state.messages;
		if (msgs.length && msgs[msgs.length - 1].role === "assistant") msgs.pop();
	},
	reset_all(state) {
		clearBurstTimer();
		state.streaming = false;
		state.open = false;
		state.conversationId = null;
		state.messages = [];
		state.progress = [];
		state.usage = null;
		state.lastSearch = null;
		state.pendingSearch = null;
		state.formDirty = false;
		state.formFilters = null;
		state.openProduct = null;
		state.notice = null;
		state.error = null;
		state.view = "chat";
		state.conversations = [];
		state.conversationsTotal = 0;
		state.conversationsSearch = "";
		state.conversationsPage = 1;
		state.conversationsLoading = false;
		state.readOnly = false;
		state.title = null;
		state.titleSetByUser = false;
		state.pendingTitle = null;
		state.pendingTitleSetByUser = false;
		state.listError = null;
	},
	set_streaming(state, streaming) {
		state.streaming = streaming;
		if (!streaming) state.progress = [];
	},
	set_progress(state, label) {
		state.progress = [label];
	},
	set_usage(state, usage) {
		state.usage = usage;
	},
	set_last_search(state, search) {
		state.lastSearch = search;
	},
	set_pending_search(state, filters) {
		state.pendingSearch = filters;
	},
	set_form_dirty(state, { dirty, filters }) {
		state.formDirty = dirty;
		state.formFilters = filters;
	},
	set_open_product(state, product) {
		state.openProduct = product;
	},
	set_notice(state, notice) {
		state.notice = notice;
	},
	set_error(state, error) {
		state.error = error;
	},
	set_view(state, view) {
		state.view = view;
	},
	set_conversations(state, { rows, totalCount, page }) {
		state.conversations = page === 1 ? rows : [...state.conversations, ...rows];
		state.conversationsTotal = totalCount;
		state.conversationsPage = page;
	},
	set_conversations_loading(state, loading) {
		state.conversationsLoading = loading;
	},
	set_conversations_search(state, search) {
		state.conversationsSearch = search;
	},
	set_read_only(state, readOnly) {
		state.readOnly = readOnly;
	},
	set_title(state, title) {
		state.title = title;
	},
	set_title_set_by_user(state, value) {
		state.titleSetByUser = !!value;
	},
	set_list_error(state, text) {
		state.listError = text;
	},
	// Clearing the pending title clears its flag, so every existing set_pending_title(null) commit stays correct.
	set_pending_title(state, title) {
		state.pendingTitle = title;
		if (!title) state.pendingTitleSetByUser = false;
	},
	set_pending_title_set_by_user(state, value) {
		state.pendingTitleSetByUser = !!value;
	},
	// Saved messages get string ids ("m" + messageId); streamed ones keep numeric ids from nextId, so they never collide.
	set_transcript(state, messages) {
		state.messages = (messages || []).map((m) => ({
			id: "m" + m.messageId,
			role: m.role,
			text: m.text || "",
			cards: [],
			tools: m.tools || [],
			truncated: !!m.truncated,
			stopped: false,
			// The server sends saved suggestions on the last answer only. null (never generated) stays distinct from
			// [] (generated, nothing to suggest): only null makes the drawer ask the server once.
			suggestions: Array.isArray(m.suggestions) ? m.suggestions.filter((r) => typeof r === "string") : null,
		}));
	},
	remove_conversation(state, id) {
		const before = state.conversations.length;
		state.conversations = state.conversations.filter((c) => c.conversationId !== id);
		if (state.conversations.length < before)
			state.conversationsTotal = Math.max(0, state.conversationsTotal - 1);
	},
};

const actions = {
	toggle({ commit, state }) {
		commit("set_open", !state.open);
	},
	close({ commit }) {
		commit("set_open", false);
	},
	newConversation({ commit, dispatch }) {
		dispatch("stop");
		discardText();
		openRequest++;
		commit("reset_conversation");
		commit("set_pending_title", null);
	},
	showList({ commit, dispatch }) {
		commit("set_list_error", null);
		commit("set_view", "list");
		return dispatch("loadConversations", { page: 1 });
	},
	showChat({ commit }) {
		// Going back cancels an open that has not landed yet.
		openRequest++;
		commit("set_view", "chat");
	},
	// List actions report through listError only; state.error belongs to the chat (a turn error keeps its Retry).
	async loadConversations({ commit, dispatch, state }, { page }) {
		const request = ++listRequest;
		commit("set_conversations_loading", true);
		try {
			const res = await axios.get("/api/assistant/conversations", {
				params: { search: state.conversationsSearch || undefined, page, pageSize: 50 },
			});
			if (request === listRequest) {
				commit("set_conversations", { rows: res.data.rows, totalCount: res.data.totalCount, page });
				commit("set_list_error", null);
			}
		} catch (e) {
			if (request !== listRequest) return;
			if (endSessionOnAccessError(commit, dispatch, e.response?.status)) return;
			commit("set_list_error", "Could not load your conversations.");
		} finally {
			if (request === listRequest) commit("set_conversations_loading", false);
		}
	},
	async openConversation({ commit, dispatch, state }, id) {
		const busyText = "Wait for the current answer to finish before opening another conversation.";
		if (state.streaming) {
			commit("set_list_error", busyText);
			return;
		}
		const request = ++openRequest;
		let res;
		try {
			res = await axios.get(`/api/assistant/conversations/${id}`);
		} catch (e) {
			if (request !== openRequest) return;
			if (endSessionOnAccessError(commit, dispatch, e.response?.status)) return;
			if (e.response?.status === 404) {
				await dispatch("loadConversations", { page: 1 });
				commit("set_list_error", "That conversation is no longer available.");
			} else commit("set_list_error", "Could not open that conversation.");
			return;
		}
		// Only the latest open may land, and never into a turn that started while the request was out.
		if (request !== openRequest) return;
		if (state.streaming) {
			commit("set_list_error", busyText);
			return;
		}
		commit("reset_conversation");
		commit("set_pending_title", null);
		commit("set_conversation", id);
		commit("set_title", res.data.conversation.title);
		commit("set_title_set_by_user", res.data.conversation.titleSetByUser);
		commit("set_transcript", res.data.messages);
		commit("set_read_only", !!res.data.conversation.isClosed);
		if (!res.data.conversation.isClosed && !res.data.turnInProgress) dispatch("loadSuggestions", id);
		if (res.data.turnInProgress)
			commit("set_error", {
				text: "The assistant is still answering in another tab.",
				retryable: false,
				lastText: "",
			});
		commit("set_view", "chat");
	},
	async renameConversation({ commit, dispatch, state }, { id, title }) {
		try {
			await axios.patch(`/api/assistant/conversations/${id}`, { title });
		} catch (e) {
			const status = e.response?.status;
			if (endSessionOnAccessError(commit, dispatch, status)) return;
			await dispatch("loadConversations", { page: 1 });
			commit(
				"set_list_error",
				status === 400
					? e.response.data?.message || "That title cannot be used."
					: status === 404
					? "That conversation is no longer available."
					: "Could not rename the conversation."
			);
			return;
		}
		if (state.conversationId === id) {
			commit("set_title", title);
			commit("set_title_set_by_user", true);
		}
		return dispatch("loadConversations", { page: 1 });
	},
	async deleteConversation({ commit, dispatch, state }, id) {
		const busyText =
			"The assistant is still answering in that conversation. Wait for it to finish, then delete it.";
		if (state.streaming && state.conversationId === id) {
			commit("set_list_error", busyText);
			return;
		}
		try {
			await axios.delete(`/api/assistant/conversations/${id}`);
		} catch (e) {
			if (endSessionOnAccessError(commit, dispatch, e.response?.status)) return;
			if (e.response?.status === 409) {
				commit("set_list_error", busyText);
				return;
			}
			// 404: already gone (deleted in another tab); anything else is a real failure.
			if (e.response?.status !== 404) {
				commit("set_list_error", "Could not delete the conversation.");
				return;
			}
		}
		commit("remove_conversation", id);
		if (state.conversationId === id) {
			// The reset returns the drawer to an empty chat; stay on the list the user is working in.
			const view = state.view;
			cancelPendingLoads(commit);
			commit("reset_conversation");
			commit("set_view", view);
		}
		return dispatch("loadConversations", { page: 1 });
	},
	continueInNew({ commit, state }) {
		const title = state.title;
		const titleSetByUser = state.titleSetByUser;
		openRequest++;
		commit("reset_conversation");
		commit("set_pending_title", title);
		commit("set_pending_title_set_by_user", titleSetByUser);
	},
	async loadUsage({ commit }) {
		try {
			const res = await axios.get("/api/assistant/usage");
			commit("set_usage", res.data);
		} catch (e) {
			// usage is decorative; the server enforces it
		}
	},
	// Product Search reports the product shown in its detail view (null when it closes) so the model can start from it.
	setOpenProduct({ commit }, product) {
		commit("set_open_product", product && product.productId ? product : null);
	},
	reportFormEdit({ commit }, filters) {
		commit("set_form_dirty", { dirty: true, filters });
	},
	clearFormDirty({ commit }) {
		commit("set_form_dirty", { dirty: false, filters: null });
	},
	showInSearch({ commit }, filters) {
		commit("set_pending_search", filters);
	},
	consumePendingSearch({ commit, state }) {
		const pending = state.pendingSearch;
		commit("set_pending_search", null);
		return pending;
	},
	stop({ commit }) {
		if (controller) controller.abort();
		abortTail();
		// The stream may already be closed with text still revealing; show the rest now so Stop always does something.
		flushText(commit);
	},
	// The drawer reports when the composer goes from empty to typed and back: typing means the user has their own
	// next message, so the suggestions go and none are applied until the composer is empty again.
	setDraftEmpty({ commit }, empty) {
		commit("set_draft_empty", empty);
		if (!empty) commit("clear_suggestions");
	},
	// An open conversation whose latest answer never had suggestions generated (answered before they existed, or the
	// live call found none or failed): ask once; the server generates and saves them (an empty list included).
	async loadSuggestions({ commit, state }, id) {
		const message = state.messages[state.messages.length - 1];
		if (!message || message.role !== "assistant" || message.truncated || message.suggestions !== null) return;
		try {
			const res = await axios.post(`/api/assistant/conversations/${id}/suggestions`);
			const replies = (Array.isArray(res.data?.suggestions) ? res.data.suggestions : [])
				.filter((r) => typeof r === "string" && r.trim())
				.slice(0, 3);
			// Only if the same conversation is still open and idle; set_suggestions also checks the message is still last.
			if (replies.length && state.conversationId === id && !state.streaming && !state.readOnly)
				commit("set_suggestions", { message, replies });
		} catch (e) {
			// Suggestions are optional; a failure only means none are shown.
		}
	},
	resetAll({ commit, dispatch }) {
		dispatch("stop");
		discardText();
		cancelPendingLoads(commit);
		commit("reset_all");
	},
	// After a 403 the drawer shows why until the user navigates; then it is cleared and hidden.
	applyRevokedAccess({ commit, dispatch }) {
		dispatch("stop");
		cancelPendingLoads(commit);
		commit("reset_all");
		commit("system_set_assistant_enabled", false, { root: true });
	},
	async send({ commit, state }, { text, route, crossReferenceId, retry }) {
		const hadTurnError = !!(state.error && state.error.turnKept);
		const trimmed = (text || "").trim();
		if (!trimmed || state.streaming) return;
		// A closed conversation has no composer; the read-only notice offers "Continue in a new conversation".
		if (state.readOnly) return;
		abortTail();
		commit("clear_suggestions");
		clearBurstTimer();
		commit("set_error", null);
		commit("set_notice", null);

		if (!state.conversationId) {
			try {
				const title = state.pendingTitle || trimmed.slice(0, 120);
				const titleSetByUser = !!state.pendingTitle && state.pendingTitleSetByUser;
				const res = await axios.post("/api/assistant/conversations", { title, titleSetByUser });
				commit("set_conversation", res.data.conversationId);
				commit("set_title", title);
				commit("set_title_set_by_user", titleSetByUser);
				commit("set_pending_title", null);
			} catch (e) {
				if (e.response?.status === 403) {
					// Say why before the drawer goes away; it is cleared and hidden on the next navigation.
					cancelPendingLoads(commit);
					commit("reset_conversation");
					commit("set_notice", { kind: "off", text: REVOKED_TEXT });
				} else
					commit("set_error", {
						text: "Could not start a conversation.",
						retryable: true,
						lastText: trimmed,
					});
				return;
			}
		}

		const last = state.messages[state.messages.length - 1];
		const prev = state.messages[state.messages.length - 2];
		// Reuse this turn's bubbles only when an interrupted or errored turn left them in place: the user bubble with
		// its assistant bubble, or (after a burst limit) the user bubble alone.
		const reuseBoth =
			last && last.role === "assistant" && prev && prev.role === "user" && prev.text === trimmed;
		const reuseUser = last && last.role === "user" && last.text === trimmed;
		const reuse = retry && hadTurnError && (reuseBoth || reuseUser);
		if (reuse && reuseBoth) commit("drop_last_assistant");
		else if (!reuse) commit("push_user", trimmed);
		commit("push_assistant");
		const assistantMessage = state.messages[state.messages.length - 1];
		commit("set_streaming", true);
		const myController = new AbortController();
		controller = myController;
		let doneSeen = false;
		let truncated = false;
		let suggestions = [];
		// Set by the done event: the answer is complete and stored. Once its text has revealed, the turn is released
		// (streaming off, composer free) while the reader waits for the suggestions that follow done.
		let donePromise = null;
		let released = false;
		const applySuggestions = () => {
			if (!truncated && suggestions.length)
				commit("set_suggestions", { message: assistantMessage, replies: suggestions });
		};
		const releaseAtDone = async () => {
			await textDrained();
			if (controller !== myController) return;
			released = true;
			if (truncated) commit("set_truncated");
			commit("set_form_dirty", { dirty: false, filters: null });
			controller = null;
			tailController = myController;
			commit("set_streaming", false);
			applySuggestions();
		};
		const pageContext = {
			route: route || null,
			filters: state.formDirty ? state.formFilters : null,
			formDirty: state.formDirty,
			product: state.openProduct,
			crossReferenceId: crossReferenceId || null,
		};
		try {
			await streamMessage(
				state.conversationId,
				{ text: trimmed, pageContext },
				(name, data) => {
					switch (name) {
						case "progress":
							commit("set_progress", data.label);
							commit("push_tool", data.label);
							break;
						case "text":
							enqueueText(commit, data.delta);
							break;
						case "search":
							commit("push_card", {
								type: "search",
								filters: data.filters,
								totalCount: data.totalCount,
								note: data.note,
							});
							break;
						case "restart":
							discardText();
							commit("clear_assistant_text");
							break;
						case "attachment":
							commit("push_card", {
								type: "attachment",
								fileName: data.fileName,
								url: data.url,
								kind: data.kind,
							});
							break;
						case "faq":
							commit("push_card", { type: "faq", count: data.count });
							break;
						case "usage": {
							// Merge so a field the event lacks keeps its value from the usage GET.
							const merged = { ...(state.usage || {}) };
							["used", "limit", "remaining", "resetsOn", "exempt"].forEach((key) => {
								if (data[key] !== undefined) merged[key] = data[key];
							});
							commit("set_usage", merged);
							break;
						}
						case "suggestions":
							// Held until the reveal has caught up, so the chips never appear under half-shown text.
							suggestions = (Array.isArray(data.replies) ? data.replies : [])
								.filter((r) => typeof r === "string" && r.trim())
								.slice(0, 3);
							if (released) applySuggestions();
							break;
						case "done":
							doneSeen = true;
							truncated = !!data.truncated;
							if (!donePromise) donePromise = releaseAtDone();
							break;
						case "error":
							doneSeen = true;
							commit("set_error", {
								text: data.message,
								retryable: data.retryable,
								lastText: trimmed,
								turnKept: true,
							});
							break;
						default:
							break;
					}
				},
				myController.signal
			);
			// Let the reveal catch up before the turn is marked finished, or a burst that arrived with the done event
			// would land all at once.
			await textDrained();
			if (donePromise) {
				await donePromise;
				return;
			}
			if (truncated) commit("set_truncated");
			if (!doneSeen) {
				// The stream closed without done or error: a dropped connection, not a finished answer.
				commit("set_truncated");
				commit("set_error", {
					text: "Answer interrupted.",
					retryable: true,
					lastText: trimmed,
					turnKept: true,
				});
			}
			commit("set_form_dirty", { dirty: false, filters: null });
		} catch (e) {
			// Once released, the queue may belong to the next turn; leave it alone.
			if (!released) flushText(commit);
			if (donePromise) {
				// After done: a Stop during the reveal, the next send aborting the tail, or a dropped tail. The answer is
				// complete, so it is never marked truncated or interrupted.
				await donePromise;
			} else if (e.name === "AbortError") {
				commit("set_truncated");
			} else if (e.status === 429 && e.body && e.body.code === "BURST") {
				// A short wait, not the end of the conversation: keep the user bubble and the composer, show the wait as
				// a retryable error row, and when the wait is over keep the row (with Retry) and say the user can retry.
				const seconds = e.body.retryAfterSeconds || 60;
				commit("drop_last_assistant");
				const burstError = {
					text: `You are sending messages quickly. Try again in ${seconds} seconds.`,
					retryable: true,
					lastText: trimmed,
					turnKept: true,
				};
				commit("set_error", burstError);
				clearBurstTimer();
				burstTimer = setTimeout(() => {
					burstTimer = null;
					if (state.error === burstError)
						commit("set_error", { ...burstError, text: "You can try again now." });
				}, seconds * 1000);
			} else if (e.status === 429 && e.body && e.body.code === "MONTHLY") {
				const b = e.body;
				const text = `You have used this month's ${b.limit || ""} questions. The allotment resets on ${
					b.resetsOn ? new Date(b.resetsOn).toLocaleDateString() : "the first of next month"
				}.`;
				commit("set_notice", { kind: "quota", text });
				commit("drop_last_turn");
			} else if (e.status === 429) {
				// The site-wide rate limit middleware, not the assistant quota.
				commit("drop_last_turn");
				commit("set_error", {
					text: "Too many requests right now. Wait a moment and try again.",
					retryable: true,
					lastText: trimmed,
				});
			} else if (e.status === 403) {
				// Say why before the drawer goes away; it is cleared and hidden on the next navigation.
				commit("drop_last_turn");
				cancelPendingLoads(commit);
				commit("reset_conversation");
				commit("set_notice", { kind: "off", text: REVOKED_TEXT });
			} else if (e.status === 409 && e.body?.code === "CLOSED") {
				// Closed while open (in another tab, or by the server): keep the transcript and switch to read-only;
				// the read-only notice offers "Continue in a new conversation".
				commit("drop_last_turn");
				commit("set_read_only", true);
			} else if (e.status === 404) {
				commit("drop_last_turn");
				openRequest++;
				commit("reset_conversation");
				commit("set_notice", { kind: "closed", text: "This conversation was deleted." });
			} else if (e.status === 409) {
				commit("drop_last_turn");
				commit("set_error", {
					text: "The assistant is still answering your last message.",
					retryable: false,
					lastText: trimmed,
				});
			} else if (e.status === 400) {
				commit("drop_last_turn");
				commit("set_error", {
					text: e.body?.message || "That message could not be sent.",
					retryable: false,
					lastText: trimmed,
				});
			} else if (e.status === 401) {
				// The app has no global 401 handler; end the assistant session and ask the user to sign in again.
				commit("drop_last_turn");
				cancelPendingLoads(commit);
				commit("reset_conversation");
				commit("set_notice", { kind: "auth", text: AUTH_TEXT });
			} else {
				commit("set_error", {
					text: "Answer interrupted.",
					retryable: true,
					lastText: trimmed,
					turnKept: true,
				});
			}
		} finally {
			if (controller === myController) {
				controller = null;
				commit("set_streaming", false);
			}
			if (tailController === myController) tailController = null;
		}
	},
};

export default { namespaced: true, state, getters, mutations, actions };
